import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment";
import { Article } from "src/app/interfaces/article";
import { ArticleModalComponent } from "../../modal-components/article-modal/article-modal.component";
import { PreorderArticleModalComponent } from "../../modal-components/preorder-article-modal/preorder-article-modal.component";
import { ApplicationService } from "src/app/services/application.service";
import { SettingsService } from "src/app/services/settings.service";

@Component({
    selector: "app-preorder-month-input",
    templateUrl: "./preorder-month-input.component.html",
    styleUrls: ["./preorder-month-input.component.scss"],
    standalone: false
})
export class PreorderMonthInputComponent implements OnInit {
  @Input() optionArticle: Article;
  @Input() month: any = {};
  @Input() value: number;
  @Input() inputIndex: number;
  @Input() brand: string;
  @Input() type: string;
  @Input() max: string = "99";
  @Input() min: string = "1";
  @Input() monthSelectionOnly: boolean = false;
  @Output() checkQuantity: EventEmitter<any> = new EventEmitter<any>();
  quantities = [];
  tempQuantities = [];
  companyGroup: string;
  hasInputValue: boolean = false;
  showOrBeforeOption: boolean = false;
  constructor(private modalService: NgbModal, private applicationService: ApplicationService, private settingsService: SettingsService) {}

  ngOnInit() {
    this.companyGroup = this.applicationService.getSelectCompanyGroupCode();
    this.hasInputValue = !!this.value;
    this.checkOrBeforeOption();
  }

  /**
   * Checks if the input would normally be disabled based on stock conditions
   */
  private isStockConditionMet(): boolean {

    return (this.optionArticle.always_order && this.optionArticle.stock_status === 4) || (this.optionArticle.stock_status >= 3 &&
      ((this.optionArticle.stock_week > (this.month.month === 12 && this.month.week === 1 ? 52 : this.month.week) &&
        this.month.year == this.optionArticle.stock_year || this.optionArticle.stock_year === 9999 && !this.optionArticle.always_order) ||
        this.optionArticle.stock_year > this.month.year) &&
      (!this.optionArticle.always_order || this.optionArticle.stock_status === 3));
  }

   checkOrBeforeOption() {
      // Only show "or before" option for bicycles and when enabled in settings
      const b2bSettings = this.settingsService.getB2bLocalSettings(
        this.applicationService.getSelectCompanyGroupCode()
      );
      this.showOrBeforeOption = 
        b2bSettings?.ordering?.show_or_before_week_option === true && 
        this.optionArticle?.article_type !== 'P';
    }

  /**
   * Returns true if input should be disabled.
   * If stock_status < 4, the input remains editable (but with warning styling).
   */
  isInputDisabled(): boolean {
    // Only disable if stock_status >= 4 and stock condition is met
    if (this.optionArticle.stock_status >= 3 && this.isStockConditionMet() && !this.showOrBeforeOption) {
      return true;
    }

    return false;
  }

  /**
   * Returns the CSS class for the input based on stock conditions.
   * Adds warning class when status < 4 but stock condition is met.
   */
  getInputClass(): string {
    if (this.optionArticle.stock_status >= 3 && this.isStockConditionMet() && !this.showOrBeforeOption) {
      return 'disabled';
    }
    if (this.optionArticle.stock_status <= 4 && this.isStockConditionMet() && this.showOrBeforeOption && this.inputIndex !== 0) {
      return 'enabledInput warningInput';
    }
    return 'enabledInput';
  }

  /**
   * Returns true if warning tooltip should be shown.
   * Shows when status < 4, stock condition is met, and input has a value.
   */
  showWarningTooltip(): boolean {
    return this.optionArticle.stock_status < 4 && 
           this.isStockConditionMet() && 
           this.hasInputValue;
  }

  quantityChange(data) {
    if (Number(data.target.value) > Number(this.max)) {
      data.target.value = this.max;
    }
    // Track if input has value for warning tooltip
    this.hasInputValue = !!data.target.value && Number(data.target.value) > 0;
    this.checkQuantity.emit();
  }

  firstWeek() {
    return moment(
      `01-${this.month.month}-${this.month.year}`,
      "DD-MM-YYYY"
    ).isoWeek();
  }

  openModal(article, event) {
    const modalRef = this.modalService.open(PreorderArticleModalComponent, {
      size: "lg",
      centered: true,
      backdrop: "static",
      container: "#modalContainer",
    });
    modalRef.componentInstance.article = article;
    modalRef.componentInstance.modelId = article["id"];
    modalRef.componentInstance.showDetailed = true;
    modalRef.componentInstance.preOrder = true;
    modalRef.componentInstance.showInModal = true;
    modalRef.componentInstance.month = this.month;
    modalRef.componentInstance.selectedArticle = article["single_article_code"];
    modalRef.componentInstance.article.img = article["images"];
    modalRef.componentInstance.showInstant();
    modalRef.componentInstance.initQuantities = [
      ...(this.quantities.length ? this.quantities : this.tempQuantities),
    ];
    modalRef.componentInstance.submitObserver.subscribe((response: any) => {
      this.quantities = response;
      this.checkQuantity.emit(1);
    });
    event.preventDefault();
  }

  add() {
    if (!this.tempQuantities.length || this.tempQuantities.length > 1) {
      const quantity = {
        week: this.firstWeek(),
        year: this.month.year,
        quantity: 1,
      };
      this.tempQuantities = [quantity];
    } else {
      this.tempQuantities[0]["quantity"]++;
    }
  }

  decrease() {
    if (this.tempQuantities.length || this.tempQuantities.length > 1) {
      this.tempQuantities[0]["quantity"] =
        this.tempQuantities[0]["quantity"] > 1
          ? this.tempQuantities[0]["quantity"] - 1
          : 0;
    }
  }

  closeTooltip(tooltip: NgbTooltip) {
    this.quantities = this.tempQuantities;
    tooltip.close();
  }

  get disableInputField(): boolean {

    const alwaysOrder = this.optionArticle.always_order;
    const outOfStock = this.optionArticle.stock_status >= 3;
    const noStockDate =
      this.optionArticle.stock_week === 99 ||
      this.optionArticle.stock_year === 9999;
    return !alwaysOrder && (outOfStock || noStockDate);
  }
}
